import type { BindingState, RebindResponse } from '@/lib/api/auth';

type Rebind = () => Promise<Pick<RebindResponse, 'bindingState'>>;

interface ResolveUnknownOAuthBindingOptions {
    delaysMs?: readonly number[];
}

const DEFAULT_RETRY_DELAYS_MS = [250, 750] as const;

function wait(delayMs: number): Promise<void> {
    if (delayMs <= 0) return Promise.resolve();
    return new Promise((resolve) => window.setTimeout(resolve, delayMs));
}

/**
 * Retries only the authoritative read/rebind endpoint. An unavailable lookup
 * must never be interpreted as permission to start an owner transaction.
 */
export async function resolveUnknownOAuthBinding(
    rebind: Rebind,
    options: ResolveUnknownOAuthBindingOptions = {},
): Promise<BindingState> {
    const delaysMs = options.delaysMs ?? DEFAULT_RETRY_DELAYS_MS;
    for (let attempt = 0; attempt <= delaysMs.length; attempt += 1) {
        try {
            const result = await rebind();
            if (result.bindingState !== 'unknown') {
                return result.bindingState;
            }
        } catch {
            // A transient failure remains unknown and is retried read-only.
        }
        if (attempt < delaysMs.length) await wait(delaysMs[attempt]!);
    }
    return 'unknown';
}
